type CaretIconProps = {
  direction: 'left' | 'right' | 'up' | 'down';
  color?:
    | 'purple'
    | 'gray'
    | 'white'
    | 'darkPurple'
    | 'current'
    | 'interactiveLink';
  size?: 'small' | 'large';
};

function CaretIcon({
  direction,
  color = 'purple',
  size = 'small',
}: CaretIconProps) {
  const COLORS = {
    purple: 'var(--purple-700)',
    gray: 'var(--gray-700)',
    white: 'white',
    darkPurple: 'var(--purple-900)',
    // For buttons with text and icon - tracks color of nearest ancestor
    // Text and icon match in different states (hover, active, etc)
    current: 'currentColor',
    interactiveLink: 'var(--interactive-link)',
  };
  const viewbox =
    size === 'small' || direction === 'left' ? '0 0 10 18' : '0 0 24 24';
  const [width, height] = size === 'small' ? ['10', '18'] : ['24', '24'];

  const PATHS = {
    down: 'M16.9132 8.1633L10.6632 14.4133C10.5762 14.5007 10.4727 14.57 10.3587 14.6174C10.2447 14.6647 10.1226 14.689 9.99918 14.689C9.8758 14.689 9.75362 14.6647 9.63967 14.6174C9.52571 14.57 9.42222 14.5007 9.33512 14.4133L3.08512 8.1633C2.909 7.98718 2.81006 7.74831 2.81006 7.49923C2.81006 7.25016 2.909 7.01129 3.08512 6.83517C3.26124 6.65905 3.50011 6.56011 3.74918 6.56011C3.99826 6.56011 4.23713 6.65905 4.41325 6.83517L9.99997 12.4219L15.5867 6.83439C15.7628 6.65827 16.0017 6.55933 16.2507 6.55933C16.4998 6.55933 16.7387 6.65827 16.9148 6.83439C17.0909 7.01051 17.1899 7.24938 17.1899 7.49845C17.1899 7.74752 17.0909 7.98639 16.9148 8.16252L16.9132 8.1633Z',
    left: 'M9.42464 15.3301C9.63598 15.5414 9.75471 15.8281 9.75471 16.127C9.75471 16.4258 9.63598 16.7125 9.42464 16.9238C9.21329 17.1352 8.92665 17.2539 8.62776 17.2539C8.32888 17.2539 8.04223 17.1352 7.83089 16.9238L0.330887 9.42383C0.226007 9.31931 0.142791 9.19512 0.0860095 9.05837C0.0292284 8.92163 0 8.77502 0 8.62695C0 8.47889 0.0292284 8.33228 0.0860095 8.19553C0.142791 8.05878 0.226007 7.93459 0.330887 7.83008L7.83089 0.330076C8.04223 0.118732 8.32888 -4.45375e-09 8.62776 0C8.92665 4.45375e-09 9.21329 0.118732 9.42464 0.330077C9.63598 0.541421 9.75471 0.828065 9.75471 1.12695C9.75471 1.42584 9.63598 1.71248 9.42464 1.92383L2.72245 8.62601L9.42464 15.3301Z',
    up: 'M16.9132 11.8367L10.6632 5.5867C10.5762 5.4993 10.4727 5.42996 10.3587 5.38264C10.2447 5.33532 10.1226 5.31096 9.99918 5.31096C9.8758 5.31096 9.75362 5.33532 9.63967 5.38264C9.52571 5.42996 9.42222 5.4993 9.33512 5.5867L3.08512 11.8367C2.909 12.0128 2.81006 12.2517 2.81006 12.5008C2.81006 12.7498 2.909 12.9887 3.08512 13.1648C3.26124 13.3409 3.50011 13.4399 3.74918 13.4399C3.99826 13.4399 4.23713 13.3409 4.41325 13.1648L9.99997 7.57811L15.5867 13.1656C15.7628 13.3417 16.0017 13.4407 16.2507 13.4407C16.4998 13.4407 16.7387 13.3417 16.9148 13.1656C17.0909 12.9895 17.1899 12.7506 17.1899 12.5015C17.1899 12.2525 17.0909 12.0136 16.9148 11.8375L16.9132 11.8367Z',
    right:
      'M17.2959 12.7959L9.7959 20.2959C9.58455 20.5072 9.29791 20.626 8.99902 20.626C8.70014 20.626 8.41349 20.5072 8.20215 20.2959C7.9908 20.0846 7.87207 19.7979 7.87207 19.499C7.87207 19.2001 7.9908 18.9135 8.20215 18.7021L14.9062 12L8.20402 5.2959C8.09937 5.19125 8.01636 5.06702 7.95973 4.93029C7.90309 4.79356 7.87394 4.64702 7.87394 4.49902C7.87394 4.35103 7.90309 4.20448 7.95973 4.06776C8.01636 3.93103 8.09937 3.80679 8.20402 3.70215C8.30867 3.5975 8.4329 3.51449 8.56963 3.45785C8.70636 3.40122 8.8529 3.37207 9.0009 3.37207C9.14889 3.37207 9.29543 3.40122 9.43216 3.45785C9.56889 3.51449 9.69312 3.5975 9.79777 3.70215L17.2978 11.2021C17.4025 11.3068 17.4856 11.4311 17.5422 11.5679C17.5988 11.7047 17.6279 11.8513 17.6277 11.9994C17.6275 12.1475 17.5981 12.2941 17.5412 12.4307C17.4843 12.5674 17.4009 12.6915 17.2959 12.7959Z',
  };
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={viewbox}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[direction]} fill={COLORS[color]} />
    </svg>
  );
}

export default CaretIcon;
